/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  ClosedCaptionBackgroundColor,
  ClosedCaptionColor,
  ClosedCaptionFontFamily,
  ClosedCaptionFontSize,
  ClosedCaptionPreset
} from "../enums";
import type { TClosedCaptionStyle } from "../types";

export const CC_BASE_FONT_SIZE_VW_TV: number = 1.25;

export const CC_BASE_FONT_SIZE_VW_BROWSER: number = 0.9167;

/**
 * Resolve the base caption font size (vw) to scale preset multipliers
 * against, based on whether the app is running on a TV device.
 *
 * @param isTvDevice - Whether the app is running on a SmartTV device.
 * @returns {CC_BASE_FONT_SIZE_VW_TV} when `isTvDevice` is true, otherwise
 *   {CC_BASE_FONT_SIZE_VW_BROWSER}.
 */
export function getClosedCaptionBaseFontSizeVw(isTvDevice: boolean): number {
  return isTvDevice ? CC_BASE_FONT_SIZE_VW_TV : CC_BASE_FONT_SIZE_VW_BROWSER;
}

/**
 * Preset selected when no user choice has been made yet.
 */
export const DEFAULT_CLOSED_CAPTION_PRESET: ClosedCaptionPreset =
  ClosedCaptionPreset.Default;

/**
 * Registry of selectable closed caption configuration sets.
 *
 * The user selects a {ClosedCaptionPreset} option and the corresponding
 * complete style is applied to the Shaka caption cues. New configurations are
 * added as entries here once finalized. The default preset is medium-size white
 * text on a solid black background.
 */
export const CLOSED_CAPTION_PRESETS: Record<
  ClosedCaptionPreset,
  TClosedCaptionStyle
> = {
  [ClosedCaptionPreset.Default]: {
    backgroundColor: ClosedCaptionBackgroundColor.SolidBlack,
    color: ClosedCaptionColor.White,
    fontFamily: ClosedCaptionFontFamily.Default,
    fontSizeMultiplier: ClosedCaptionFontSize.Medium,
    fontStyle: "normal",
    fontVariant: "normal",
    fontWeight: "normal",
    opacity: 1
  },
  [ClosedCaptionPreset.HighContrast]: {
    backgroundColor: ClosedCaptionBackgroundColor.SolidBlack,
    color: ClosedCaptionColor.White,
    fontFamily: ClosedCaptionFontFamily.Default,
    fontSizeMultiplier: ClosedCaptionFontSize.Medium,
    fontStyle: "normal",
    fontVariant: "normal",
    fontWeight: "bold",
    opacity: 1
  },
  [ClosedCaptionPreset.LargeYellow]: {
    backgroundColor: ClosedCaptionBackgroundColor.SemiTransparentBlack,
    color: ClosedCaptionColor.Yellow,
    fontFamily: ClosedCaptionFontFamily.Default,
    fontSizeMultiplier: ClosedCaptionFontSize.ExtraLarge,
    fontStyle: "normal",
    fontVariant: "normal",
    fontWeight: "bold",
    opacity: 1
  }
};
